import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { useColors } from "../constants/Colors";
import {
  FaInstagram,
  FaShieldAlt,
  FaEdit,
  FaFlag,
  FaBan,
} from "react-icons/fa";
import { FiGrid } from "react-icons/fi";
import { HiOutlineMenu } from "react-icons/hi";
import { trpcReact } from "../trpc_client";
import { usePostHog } from "posthog-js/react";
import { getFa5Icon, getHobbyInfo } from "../constants/Hobbies";
import VideoThumbnail from "./VideoThumbnail";
import FullscreenVideoModal from "./FullscreenVideoModal";
import CompleteProfileList from "./CompleteProfileList";
import { GOOGLE_MAPS_API_KEY } from "../constants/MapStyles";
import TranslateButton from "./TranslateButton";
import { useAuth } from "../contexts/AuthContext";
import {
  MoreHorizontal,
  Globe,
  Mail,
  Instagram,
  Info,
  LogOut,
} from "lucide-react";

type ProfileData = {
  user_id?: number;
  username: string;
  age?: number | null;
  created_at?: string | Date;
  instagram_handle?: string | null;
  profile_text?: string | null;
  profile_interests?: string[] | null;
  profile_images?: string[];
  profile_video?: {
    url?: string;
    state?: string;
  };
};

type TranslationApiResponse = {
  data: {
    translations: Array<{
      translatedText: string;
    }>;
  };
};

type TranslationApiErrorResponse = {
  error?: {
    message?: string;
  };
};

interface AccountPreviewProps {
  profileData?: ProfileData;
  profileUserId?: number;
  disableMobileRedirect?: boolean;
  isOwnProfile?: boolean;
}

// Render icon based on library type
const renderIcon = (icon: string, library: string, textColor: string) => {
  switch (library) {
    case "EMOJIE":
      return <span className="text-sm mr-2.5">{icon}</span>;
    case "FontAwesome5":
      const IconComponent = getFa5Icon(icon);
      // Render the icon component
      return (
        <IconComponent
          size={14}
          style={{ color: textColor, marginRight: 10 }}
        />
      );
    default:
      return null;
  }
};

const AccountPreview: React.FC<AccountPreviewProps> = ({
  profileData,
  profileUserId,
  disableMobileRedirect = false,
  isOwnProfile = false,
}) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { t, i18n } = useTranslation();

  const {
    containerNeutralSolidSurface,
    containerNeutralLightMedium,
    accentColor,
    textNeutralDefault,
    textNeutralMuted,
    textNeutralOnPrimary,
    containerNeutralLightLowest,
  } = useColors();

  const { signOut } = useAuth();
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const [activeView, setActiveView] = useState<"grid" | "list">("grid");
  const [scrollToImageIndex, setScrollToImageIndex] = useState<number | null>(
    null,
  );
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [showSafetyMenu, setShowSafetyMenu] = useState(false);
  const [safetyActionMessage, setSafetyActionMessage] = useState<string | null>(
    null,
  );
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Translation state
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const [translationInitialized, setTranslationInitialized] = useState(false);
  const safetyMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleWindowScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const threshold = 300;

      if (documentHeight > window.innerHeight + threshold) {
        setShowFloatingButton(scrollPosition < documentHeight - threshold);
      } else {
        setShowFloatingButton(true);
      }
    };

    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    window.addEventListener("resize", handleWindowScroll);
    setTimeout(handleWindowScroll, 100);
    setTimeout(handleWindowScroll, 500);
    setTimeout(handleWindowScroll, 1000);

    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
      window.removeEventListener("resize", handleWindowScroll);
    };
  }, []);

  const paramUserId = userId ? parseInt(userId, 10) : undefined;
  const effectiveUserId =
    profileUserId ??
    (paramUserId && !isNaN(paramUserId) ? paramUserId : undefined);
  const shouldFetchProfile = !profileData && !!effectiveUserId;

  const { data, error, isLoading } = trpcReact.getUserProfile.useQuery(
    { user_id: effectiveUserId ?? 0 },
    { enabled: shouldFetchProfile },
  );

  const profile = (profileData as ProfileData | undefined) ?? data;
  const profileId = effectiveUserId ?? profile?.user_id;

  const blockUsersMutation = trpcReact.blockUsers.useMutation();
  const reportUsersMutation = trpcReact.reportUsers.useMutation();

  // Initialize translation state from localStorage once we have profileId
  useEffect(() => {
    if (profileId && !translationInitialized) {
      const savedText = localStorage.getItem(
        `profile_translation_${profileId}_text`,
      );
      const savedShow =
        localStorage.getItem(`profile_translation_${profileId}_show`) ===
        "true";
      const savedLang =
        localStorage.getItem(`profile_translation_${profileId}_lang`) || "en";
      if (savedText) setTranslatedText(savedText);
      setShowTranslation(savedShow);
      setSelectedLanguage(savedLang);
      setTranslationInitialized(true);
    }
  }, [profileId, translationInitialized]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        safetyMenuRef.current &&
        !safetyMenuRef.current.contains(event.target as Node)
      ) {
        setShowSafetyMenu(false);
      }
    };

    if (showSafetyMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSafetyMenu]);

  // Persist translation state to localStorage (only after initialized)
  useEffect(() => {
    if (profileId && translatedText && translationInitialized) {
      localStorage.setItem(
        `profile_translation_${profileId}_text`,
        translatedText,
      );
      localStorage.setItem(
        `profile_translation_${profileId}_lang`,
        selectedLanguage,
      );
    }
  }, [profileId, translatedText, selectedLanguage, translationInitialized]);

  useEffect(() => {
    if (profileId && translationInitialized) {
      localStorage.setItem(
        `profile_translation_${profileId}_show`,
        String(showTranslation),
      );
    }
  }, [profileId, showTranslation, translationInitialized]);

  // Translate text using Google Translate API
  const translateText = useCallback(
    async (text: string, targetLang: string) => {
      if (!text || text.trim() === "") return;

      setIsTranslating(true);
      setTranslationError(null);
      setShowLanguageSelector(false);

      try {
        const response = await fetch(
          `https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_MAPS_API_KEY}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              q: text,
              target: targetLang,
            }),
          },
        );

        if (!response.ok) {
          const errorData = (await response
            .json()
            .catch(() => null)) as TranslationApiErrorResponse | null;
          if (response.status === 403) {
            throw new Error("API_NOT_ENABLED");
          }
          throw new Error(errorData?.error?.message || "Translation failed");
        }

        const data = (await response.json()) as TranslationApiResponse;
        const translated = data.data.translations[0].translatedText;

        // Decode HTML entities that Google Translate API returns
        const textarea = document.createElement("textarea");
        textarea.innerHTML = translated;
        const decodedText = textarea.value;

        setTranslatedText(decodedText);
        setSelectedLanguage(targetLang);
        setShowTranslation(true);
      } catch (error) {
        console.error("Translation error:", error);
        if (error instanceof Error && error.message === "API_NOT_ENABLED") {
          setTranslationError(
            t("translation_api_not_enabled") ||
              "Translation API is not enabled.",
          );
        } else {
          setTranslationError(
            t("translation_error") || "Translation failed. Please try again.",
          );
        }
      } finally {
        setIsTranslating(false);
      }
    },
    [t],
  );

  const openApp = () => {
    const targetUserId = effectiveUserId ?? profile?.user_id;
    if (!targetUserId) return;
    window.location.href = `com.flateroo.flateroo://app/profile/${targetUserId}`;
  };

  const openPlayStore = () => {
    window.location.href =
      "https://play.google.com/store/apps/details?id=com.flateroo.flateroo";
  };
  const openAppStore = () => {
    window.location.href = "https://apps.apple.com/app/flateroo/id6683311358";
  };

  const openStore = () => {
    const isIphone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    posthog?.capture("open_store_from_profile", {
      isIphone,
      isAndroid,
    });
    if (isIphone) {
      openAppStore();
    } else if (isAndroid) {
      openPlayStore();
    }
  };

  useEffect(() => {
    if (disableMobileRedirect) return;
    if (!effectiveUserId && !profile?.user_id) return;
    const isIphone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isIphone || isAndroid) {
      openApp();
    }
  }, [disableMobileRedirect, effectiveUserId, profile?.user_id]);

  // Disable horizontal scrolling
  useEffect(() => {
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    document.body.classList.add("scrollbar-hide");
    document.documentElement.classList.add("scrollbar-hide");

    return () => {
      document.body.style.overflowX = "";
      document.documentElement.style.overflowX = "";
      document.body.classList.remove("scrollbar-hide");
      document.documentElement.classList.remove("scrollbar-hide");
    };
  }, []);

  // Set overscroll background color to match containerNeutralSolidSurface
  useEffect(() => {
    document.body.style.backgroundColor = containerNeutralSolidSurface;
    document.documentElement.style.backgroundColor =
      containerNeutralSolidSurface;

    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [containerNeutralSolidSurface]);

  // Scroll to image when switching to list view
  useEffect(() => {
    if (activeView === "list" && scrollToImageIndex !== null) {
      const imageId = `gallery-image-${scrollToImageIndex}`;
      const imageElement = document.getElementById(imageId);
      if (imageElement) {
        // Small delay to ensure the view has rendered
        setTimeout(() => {
          imageElement.scrollIntoView({ behavior: "smooth", block: "center" });
          setScrollToImageIndex(null);
        }, 100);
      }
    }
  }, [activeView, scrollToImageIndex]);

  // More menu click-outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target as Node)
      ) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMoreMenu]);

  if (!profile && isLoading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-4 text-center"
        style={{
          backgroundColor: containerNeutralSolidSurface,
          color: textNeutralDefault,
        }}
      >
        <p className="text-lg">Loading...</p>
      </div>
    );
  }

  if (!profile && (error || !shouldFetchProfile)) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-4 text-center"
        style={{
          backgroundColor: containerNeutralSolidSurface,
          color: textNeutralDefault,
        }}
      >
        <p className="text-lg mb-4">
          {error?.message ?? "Could not load this profile."}
        </p>
        <button
          onClick={openApp}
          className="px-4 py-2 rounded-xl font-medium"
          style={{
            backgroundColor: accentColor,
            color: textNeutralOnPrimary,
          }}
        >
          Open in the app
        </button>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const images = profile.profile_images ?? [];
  const mainImage = images[0];
  // Gallery shows all images except the first one (which is the round avatar at top)
  const galleryImages = images.slice(1);

  const profileVideo = profile.profile_video;
  const isVideoTranscoded = profileVideo?.state === "transcoded";
  const hasVideoState = profileVideo?.state !== undefined;
  const showVideoOverlay = hasVideoState && !isVideoTranscoded;

  const openInstagram = (handle: string) => {
    const username = handle.replace(/^@/, "");
    window.open(`https://www.instagram.com/${username}`, "_blank");
  };

  const handleBlockUser = async () => {
    if (!profileId || isOwnProfile) return;
    const reason =
      window.prompt("Reason for blocking this user (optional):") ||
      "Blocked from web profile";
    try {
      await blockUsersMutation.mutateAsync([
        { blocked_user_id: profileId, reason },
      ]);
      setSafetyActionMessage("User blocked.");
      setShowSafetyMenu(false);
      navigate("/");
    } catch (error) {
      console.error("Failed to block user:", error);
      setSafetyActionMessage("Could not block this user. Please try again.");
    }
  };

  const handleReportUser = async () => {
    if (!profileId || isOwnProfile) return;
    const reason = window.prompt("Why are you reporting this user?");
    if (!reason || !reason.trim()) return;
    try {
      await reportUsersMutation.mutateAsync([
        { reported_user_id: profileId, reason: reason.trim() },
      ]);
      setSafetyActionMessage("User reported and blocked.");
      setShowSafetyMenu(false);
      navigate("/");
    } catch (error) {
      console.error("Failed to report user:", error);
      setSafetyActionMessage("Could not report this user. Please try again.");
    }
  };

  const handleGridImageClick = (imageIndex: number) => {
    setScrollToImageIndex(imageIndex);
    setActiveView("list");
  };

  const currentLanguage = i18n.language?.startsWith("de") ? "de" : "en";

  const handleLanguageToggle = () => {
    const newLang = currentLanguage === "en" ? "de" : "en";
    i18n.changeLanguage(newLang);
    Cookies.set("language", newLang, { sameSite: "None", secure: true });
    setShowMoreMenu(false);
  };

  const handleMoreContact = () => {
    setShowMoreMenu(false);
    window.location.href = "mailto:support@flateroo.com";
  };

  const handleMoreInstagram = () => {
    setShowMoreMenu(false);
    window.open("https://www.instagram.com/flateroo_app/", "_blank");
  };

  const handleMoreAbout = () => {
    setShowMoreMenu(false);
    navigate("/about");
  };

  const handleMoreLogout = async () => {
    setShowMoreMenu(false);
    await signOut();
  };

  return (
    <div
      className="min-h-screen flex flex-col font-gnarly-regular relative w-full max-w-full overflow-x-hidden"
      style={{
        backgroundColor: containerNeutralSolidSurface,
        color: textNeutralDefault,
      }}
    >
      {/* Main content */}
      <div
        className="relative z-10 flex-1 w-full max-w-full overflow-hidden"
        style={{
          backgroundColor: containerNeutralSolidSurface,
          paddingTop: "2rem",
        }}
      >
        <div className="mx-auto px-0 sm:px-6 pb-24 w-full max-w-full sm:max-w-4xl">
          {/* ROUND PROFILE IMAGE AT TOP */}
          {mainImage && (
            <div className="flex justify-center mt-4 sm:mt-8 mb-6">
              <img
                className="w-48 h-48 sm:w-64 sm:h-64 rounded-full object-cover"
                src={mainImage}
                alt={`${profile.username}'s profile`}
              />
            </div>
          )}

          {/* PROFILE HEADER */}
          <div className="mb-8 text-center overflow-visible px-0 sm:px-0">
            <h1
              className="text-2xl sm:text-5xl font-semibold mb-4 break-words overflow-wrap-anywhere"
              style={{ color: textNeutralDefault, overflowWrap: "anywhere" }}
            >
              {profile.username}
              {profile.age && (
                <span
                  className="text-2xl sm:text-5xl font-semibold mb-4"
                  style={{ color: textNeutralDefault }}
                >
                  , {profile.age}
                </span>
              )}
            </h1>

            {/* Edit Profile + More button (only for own profile) */}
            {isOwnProfile && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <button
                  onClick={() => navigate("/profile/edit")}
                  className="px-4 py-2 rounded-full flex items-center gap-2 hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: accentColor,
                    color: textNeutralOnPrimary,
                  }}
                >
                  <FaEdit className="text-sm" />
                  <span className="text-sm font-medium">
                    {t("profile_tab.edit_profile.title") || "Edit Profile"}
                  </span>
                </button>

                {/* More menu — mobile only, desktop has it in the sidebar */}
                <div className="relative lg:hidden" ref={moreMenuRef}>
                  <button
                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      backgroundColor: showMoreMenu
                        ? "rgba(91, 61, 246, 0.08)"
                        : containerNeutralLightLowest,
                      color: showMoreMenu ? accentColor : textNeutralDefault,
                    }}
                  >
                    <MoreHorizontal size={20} strokeWidth={1.8} />
                  </button>

                  {showMoreMenu && (
                    <div
                      className="absolute right-0 mt-2 w-56 z-50 overflow-hidden"
                      style={{
                        backgroundColor: containerNeutralSolidSurface,
                        borderRadius: 16,
                        boxShadow:
                          "0 8px 40px rgba(2, 2, 16, 0.12), 0 0 0 1px rgba(2, 2, 16, 0.06)",
                        animation:
                          "scaleIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                        transformOrigin: "top right",
                      }}
                    >
                      <div className="p-1.5">
                        {/* Language toggle */}
                        <button
                          onClick={handleLanguageToggle}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-150"
                          style={{ color: textNeutralDefault }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(91, 61, 246, 0.06)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <Globe
                            size={17}
                            strokeWidth={1.8}
                            style={{ color: accentColor }}
                          />
                          <span className="text-sm font-medium flex-1 text-left">
                            {currentLanguage === "en" ? "Deutsch" : "English"}
                          </span>
                          <span
                            className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: "rgba(91, 61, 246, 0.08)",
                              color: accentColor,
                            }}
                          >
                            {currentLanguage.toUpperCase()}
                          </span>
                        </button>

                        <div
                          className="mx-2.5 my-1"
                          style={{
                            height: 1,
                            backgroundColor: "rgba(2, 2, 16, 0.06)",
                          }}
                        />

                        {/* Contact */}
                        <button
                          onClick={handleMoreContact}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-150"
                          style={{ color: textNeutralDefault }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(2, 2, 16, 0.04)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <Mail
                            size={17}
                            strokeWidth={1.5}
                            style={{ color: textNeutralMuted }}
                          />
                          <span className="text-sm">
                            {t("contact") || "Contact"}
                          </span>
                        </button>

                        {/* Instagram */}
                        <button
                          onClick={handleMoreInstagram}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-150"
                          style={{ color: textNeutralDefault }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(2, 2, 16, 0.04)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <Instagram
                            size={17}
                            strokeWidth={1.5}
                            style={{ color: textNeutralMuted }}
                          />
                          <span className="text-sm">
                            {t("follow_us") || "Follow us"}
                          </span>
                        </button>

                        {/* About */}
                        <button
                          onClick={handleMoreAbout}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-150"
                          style={{ color: textNeutralDefault }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(2, 2, 16, 0.04)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <Info
                            size={17}
                            strokeWidth={1.5}
                            style={{ color: textNeutralMuted }}
                          />
                          <span className="text-sm">
                            {t("about") || "About"}
                          </span>
                        </button>

                        {/* Logout */}
                        <div
                          className="mx-2.5 my-1"
                          style={{
                            height: 1,
                            backgroundColor: "rgba(2, 2, 16, 0.06)",
                          }}
                        />
                        <button
                          onClick={handleMoreLogout}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-150"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(220, 54, 46, 0.06)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <LogOut
                            size={17}
                            strokeWidth={1.5}
                            style={{ color: "#DC362E" }}
                          />
                          <span
                            className="text-sm font-medium"
                            style={{ color: "#DC362E" }}
                          >
                            {t("logout") || "Log out"}
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Badges row */}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {profile.created_at && (
                <div
                  className="px-3 py-1.5 rounded-full flex items-center gap-2"
                  style={{ backgroundColor: containerNeutralLightLowest }}
                >
                  <FaShieldAlt className="text-sm" />
                  <span
                    className="text-sm"
                    style={{ color: textNeutralDefault }}
                  >
                    {t("profile_tab.settings.options.joined") || "Joined"}:{" "}
                    {new Date(profile.created_at).toLocaleDateString()}
                  </span>
                </div>
              )}

              {profile.instagram_handle && (
                <button
                  onClick={() => openInstagram(profile.instagram_handle!)}
                  className="px-3 py-1.5 rounded-full flex items-center gap-2 hover:opacity-80 transition-opacity max-w-full overflow-hidden"
                  style={{ backgroundColor: containerNeutralLightLowest }}
                >
                  <FaInstagram className="text-sm flex-shrink-0" />
                  <span
                    className="text-sm truncate"
                    style={{ color: textNeutralDefault }}
                  >
                    {profile.instagram_handle}
                  </span>
                </button>
              )}

              {!isOwnProfile && (
                <div className="relative" ref={safetyMenuRef}>
                  <button
                    onClick={() => setShowSafetyMenu((prev) => !prev)}
                    className="w-9 h-9 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: containerNeutralLightLowest }}
                    title="More options"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ color: textNeutralDefault }}
                    >
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="19" cy="12" r="1" />
                      <circle cx="5" cy="12" r="1" />
                    </svg>
                  </button>
                  {showSafetyMenu && (
                    <div
                      className="absolute right-0 top-full mt-2 rounded-xl overflow-hidden shadow-xl z-20 min-w-[170px]"
                      style={{
                        backgroundColor: containerNeutralSolidSurface,
                        border: `1px solid ${containerNeutralLightMedium}`,
                      }}
                    >
                      <button
                        onClick={handleBlockUser}
                        disabled={blockUsersMutation.isPending}
                        className="w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-black/5 transition-colors disabled:opacity-50"
                        style={{ color: textNeutralDefault }}
                      >
                        <FaBan size={12} />
                        {blockUsersMutation.isPending
                          ? "Blocking..."
                          : "Block user"}
                      </button>
                      <button
                        onClick={handleReportUser}
                        disabled={reportUsersMutation.isPending}
                        className="w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-black/5 transition-colors disabled:opacity-50"
                        style={{ color: textNeutralDefault }}
                      >
                        <FaFlag size={12} />
                        {reportUsersMutation.isPending
                          ? "Reporting..."
                          : "Report user"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {safetyActionMessage && (
              <p className="mt-3 text-xs" style={{ color: textNeutralMuted }}>
                {safetyActionMessage}
              </p>
            )}

            {/* Complete Profile List - only shown for own profile */}
            {isOwnProfile && (
              <div className="mt-6 w-full max-w-full ">
                <CompleteProfileList profileData={profile} />
              </div>
            )}
          </div>

          {/* Bio Text Section */}
          {profile.profile_text && (
            <section className="mb-10 text-left px-4 sm:px-0">
              <div
                className="flex items-center justify-between gap-2 mb-2 max-w-full"
                style={{ alignItems: "center" }}
              >
                <h2
                  className="text-base font-semibold"
                  style={{
                    color: textNeutralDefault,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {t("description") || "Description"}
                </h2>
                <TranslateButton
                  isTranslating={isTranslating}
                  showTranslation={showTranslation}
                  showLanguageSelector={showLanguageSelector}
                  selectedLanguage={selectedLanguage}
                  translatedText={translatedText}
                  onToggleTranslation={() => setShowTranslation(false)}
                  onToggleLanguageSelector={() =>
                    setShowLanguageSelector(!showLanguageSelector)
                  }
                  onSelectLanguage={(langCode) =>
                    translateText(profile.profile_text!, langCode)
                  }
                  onCloseLanguageSelector={() => setShowLanguageSelector(false)}
                />
              </div>

              {translationError && (
                <p
                  className="text-xs mb-3 px-4 py-2.5 rounded-2xl"
                  style={{
                    backgroundColor: "rgba(220, 54, 46, 0.07)",
                    color: "#B02B25",
                  }}
                >
                  {translationError}
                </p>
              )}

              <p
                className="text-sm whitespace-pre-line leading-relaxed"
                style={{ color: textNeutralDefault }}
              >
                {showTranslation && translatedText
                  ? translatedText
                  : profile.profile_text}
              </p>

              {showTranslation && translatedText && (
                <p
                  className="mt-3 text-[10px] italic"
                  style={{ color: textNeutralMuted }}
                >
                  {t("translated_by_google") || "Translated by Google"}
                </p>
              )}
            </section>
          )}

          {/* PHOTO GALLERY - Tabs with Grid and List Views */}
          {(galleryImages.length > 0 ||
            isVideoTranscoded ||
            showVideoOverlay) && (
            <section>
              {/* Tab Switcher - only show if more than 1 item */}
              {galleryImages.length +
                (isVideoTranscoded ? 1 : 0) +
                (showVideoOverlay ? 1 : 0) >
                1 && (
                <div className="flex -mx-4 sm:-mx-6">
                  <button
                    type="button"
                    onClick={() => setActiveView("grid")}
                    className="flex-1 py-3 flex transition-colors justify-center items-center"
                    style={{
                      color:
                        activeView === "grid"
                          ? textNeutralDefault
                          : textNeutralMuted,
                      borderBottom:
                        activeView === "grid"
                          ? `2px solid ${textNeutralDefault}`
                          : "2px solid transparent",
                    }}
                  >
                    <FiGrid size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveView("list")}
                    className="flex-1 py-3 flex transition-colors justify-center items-center"
                    style={{
                      color:
                        activeView === "list"
                          ? textNeutralDefault
                          : textNeutralMuted,
                      borderBottom:
                        activeView === "list"
                          ? `2px solid ${textNeutralDefault}`
                          : "2px solid transparent",
                    }}
                  >
                    <HiOutlineMenu size={24} />
                  </button>
                </div>
              )}

              {/* Grid View */}
              {activeView === "grid" && (
                <div className="grid grid-cols-3 gap-[1px] overflow-hidden -mx-4 sm:-mx-6">
                  {/* Video thumbnail - shown first if transcoded */}
                  {isVideoTranscoded && profileVideo?.url && (
                    <VideoThumbnail
                      videoUrl={profileVideo.url}
                      onClick={() => setIsVideoModalOpen(true)}
                    />
                  )}
                  {galleryImages.map((img: string, idx: number) => (
                    <button
                      key={idx}
                      type="button"
                      className="aspect-[4/5] overflow-hidden focus:outline-none"
                      onClick={() => handleGridImageClick(idx)}
                    >
                      <img
                        src={img}
                        alt={`Gallery image ${idx + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 ease-out hover:scale-105"
                      />
                    </button>
                  ))}
                  {/* Video processing overlay for grid view */}
                  {showVideoOverlay && (
                    <div className="aspect-[4/5] overflow-hidden relative bg-black/60 flex items-center justify-center">
                      <div className="text-center px-4">
                        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                        <p
                          className="text-white text-xs"
                          style={{ color: textNeutralDefault }}
                        >
                          {t("profile_tab.video_processing") || "Processing"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* List View - Full Width */}
              {activeView === "list" && (
                <div className="flex flex-col gap-[1px] -mx-4 sm:-mx-6">
                  {/* Video thumbnail - shown first if transcoded */}
                  {isVideoTranscoded && profileVideo?.url && (
                    <VideoThumbnail
                      videoUrl={profileVideo.url}
                      onClick={() => setIsVideoModalOpen(true)}
                      className="w-full"
                    />
                  )}
                  {galleryImages.map((img: string, idx: number) => (
                    <div
                      key={idx}
                      id={`gallery-image-${idx}`}
                      className="w-full aspect-[4/5] overflow-hidden"
                    >
                      <img
                        src={img}
                        alt={`Gallery image ${idx + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 ease-out hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* HOBBIES SECTION */}
          {profile.profile_interests &&
            profile.profile_interests.length > 0 && (
              <section className="mb-10 mt-10 px-4 sm:px-0">
                <h2
                  className="text-base font-semibold mb-4 text-left"
                  style={{ color: textNeutralDefault }}
                >
                  {t("profile_tab.hobbies") || "Hobbies"}
                </h2>
                <div className="flex flex-wrap gap-2 max-w-full">
                  {profile.profile_interests.map(
                    (hobbyId: string, idx: number) => {
                      const hobby = getHobbyInfo(hobbyId);
                      return (
                        <div
                          key={idx}
                          className="px-4 py-2.5 rounded-full flex items-center max-w-full overflow-hidden"
                          style={{
                            border: `1px solid ${containerNeutralLightMedium}`,
                          }}
                        >
                          {renderIcon(
                            hobby.icon,
                            hobby.library,
                            textNeutralDefault,
                          )}
                          <span
                            className="text-sm font-medium truncate"
                            style={{ color: textNeutralDefault }}
                          >
                            {hobby.name}
                          </span>
                        </div>
                      );
                    },
                  )}
                </div>
              </section>
            )}
        </div>
      </div>


      {/* Fullscreen Video Modal */}
      {profileVideo?.url && (
        <FullscreenVideoModal
          isOpen={isVideoModalOpen}
          videoUrl={profileVideo.url}
          onClose={() => setIsVideoModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AccountPreview;
